import {
  useCreateWorkflow,
  useDeleteWorkflow,
  useDuplicateWorkflow,
  useWorkflows,
} from "@/api/workflow-hooks";
import { Button } from "@/components/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/dropdown-menu";
import { Input } from "@/components/input";
import { LoadingSpinner } from "@/components/loader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import {
  CopyIcon,
  DotsHorizontalIcon,
  PlusIcon,
  TrashIcon,
} from "@radix-ui/react-icons";
import type { WorkflowSelect } from "@repo/db/schema";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

interface ProjectRowProps {
  workflow: WorkflowSelect;
  onDuplicate: (workflow: WorkflowSelect) => void;
  onDelete: (workflow: WorkflowSelect) => void;
}

const ProjectRow = ({ workflow, onDuplicate, onDelete }: ProjectRowProps) => {
  const navigate = useNavigate();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  return (
    <>
      <TableRow
        className="hover:bg-accent hover:text-accent-foreground hover:cursor-pointer"
        onClick={() => {
          navigate(`/projects/${workflow.id}/extraction`);
        }}
      >
        <TableCell className="font-medium">{workflow.name}</TableCell>
        <TableCell className="flex justify-end items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => e.stopPropagation()}
              >
                <DotsHorizontalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate(workflow);
                }}
              >
                <CopyIcon className="mr-2 h-4 w-4" />
                <span>Duplicate</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDeleteDialogOpen(true);
                }}
              >
                <TrashIcon className="mr-2 h-4 w-4" />
                <span>Delete</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Project</DialogTitle>
          </DialogHeader>
          <div className="text-sm">
            Are you sure you want to delete "{workflow.name}"? This action
            cannot be undone.
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                onDelete(workflow);
                setIsDeleteDialogOpen(false);
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Projects: React.FC = () => {
  const { data: workflows, isLoading, isError } = useWorkflows();

  const createWorkflowMut = useCreateWorkflow();
  const duplicateWorkflowMut = useDuplicateWorkflow();
  const deleteWorkflowMut = useDeleteWorkflow();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "duplicate">(
    "create",
  );
  const [duplicateWorkflow, setDuplicateWorkflow] =
    useState<WorkflowSelect | null>(null);

  if (isError) return <div>Error loading projects</div>;

  const handleCreateProject = (_name: string) => {
    const name = _name.trim();
    if (name) {
      if (dialogMode === "create") {
        createWorkflowMut.mutate({ name });
      } else {
        if (!duplicateWorkflow) throw new Error("Project not found");

        duplicateWorkflowMut.mutateAsync({
          workflowId: duplicateWorkflow.id,
          name,
        });
      }

      setIsDialogOpen(false);
    }
  };

  return (
    <div className="flex flex-col w-full bg-background">
      <main className="flex flex-1 flex-col gap-4 mt-6 px-8">
        <Card className="mb-4">
          <CardHeader className="">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium">Projects</CardTitle>
              <Button onClick={() => setIsDialogOpen(true)}>
                <PlusIcon className="mr-2" />
                Create
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="w-full flex items-center justify-center">
                <LoadingSpinner size="small" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-lg">Name</TableHead>
                    <TableHead className="text-lg w-[100px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workflows?.items.map((workflow) => (
                    <ProjectRow
                      key={workflow.id}
                      workflow={workflow}
                      onDuplicate={(workflow) => {
                        setDialogMode("duplicate");
                        setIsDialogOpen(true);
                        setDuplicateWorkflow(workflow);
                      }}
                      onDelete={(workflow) => {
                        deleteWorkflowMut.mutate({ workflowId: workflow.id });
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
          <CardFooter>
            <div className="text-base text-muted-foreground">
              Showing <strong>{workflows?.items.length || 0}</strong> projects
            </div>
          </CardFooter>
        </Card>
        <CreateOrDuplicateProjectDialog
          isOpen={isDialogOpen}
          mode={dialogMode}
          onClose={() => setIsDialogOpen(false)}
          onSubmit={handleCreateProject}
          title={
            dialogMode === "create"
              ? "Create New Project"
              : `Duplicate "${duplicateWorkflow?.name}"`
          }
          isLoading={
            createWorkflowMut.isPending || duplicateWorkflowMut.isPending
          }
        />
      </main>
    </div>
  );
};

type CreateOrDuplicateProjectDialogProps = {
  isOpen: boolean;
  mode: "create" | "duplicate";
  onClose: () => void;
  onSubmit: (name: string) => void;
  title: string;
  isLoading: boolean;
};

const CreateOrDuplicateProjectDialog: React.FC<
  CreateOrDuplicateProjectDialogProps
> = ({ isOpen, onClose, onSubmit, title, isLoading, mode }) => {
  const [name, setName] = React.useState("");

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Project name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit(name);
          }}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(name)} disabled={isLoading}>
            {mode === "create" ? "Create" : "Duplicate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Projects;
